"use client";

import React from "react";
import { MapPin, Phone, Mail, Globe, ArrowRight } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const offices = [{
  region: "Americas",
  city: "Washington D.C.",
  type: "Global Headquarters",
  address: ["2001 L Street N.W., Suite 500", "New York, NY 10001"],
  phone: "+1(202) 964-4766",
  email: "capital@dynestate.com",
  coordinates: "38.9072° N, 77.0369° W"
}, {
  region: "Asia Pacific",
  city: "Singapore",
  type: "Regional Office",
  address: ["12 Marina View", "Singapore 018961"],
  phone: "+65 6622 8899",
  email: "sgcapital@dynestate.com",
  coordinates: "1.2789° N, 103.8536° E"
}];
export default function OfficePresence() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="office-presence" ref={ref} className={`dark bg-background text-foreground py-24 relative overflow-hidden transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-20" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-20" />
      </div>

      <div className="container mx-auto px-6 md:px-12">
        <div className="mb-16 md:mb-24 max-w-2xl">
          <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight mb-6 text-white">
            Global Presence
          </h2>
          <p className="text-muted-foreground text-lg font-light leading-relaxed">
            Strategically positioned in key financial capitals to serve our institutional partners with speed and precision.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-8 md:gap-0">
          {offices.map((office, index) => <React.Fragment key={office.city}>
              {}
              {index > 0 && <Separator orientation="horizontal" className="md:hidden bg-border/40 my-4" />}

              {}
              <div data-index={index} className="flex-1 flex flex-col group md:px-8 first:pl-0 last:pr-0">
                <div className="mb-8">
                  <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground/60 mb-2 block">
                    {office.region}
                  </span>
                  <h3 className="font-serif text-3xl text-white mb-1">
                    {office.city}
                  </h3>
                  <p className="text-sm text-muted-foreground font-medium">
                    {office.type}
                  </p>
                </div>

                <div className="space-y-6 grow">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-5 h-5 text-primary mt-1 shrink-0 opacity-70" />
                    <div className="space-y-1">
                      {office.address.map((line, i) => <p key={i} className="text-muted-foreground leading-relaxed">
                          {line}
                        </p>)}
                      <p className="text-xs font-mono text-muted-foreground/40 pt-2">
                        {office.coordinates}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <Phone className="w-5 h-5 text-primary shrink-0 opacity-70" />
                    <Link to={`tel:${office.phone.replace(/[^0-9+]/g, "")}`} className="text-muted-foreground hover:text-white transition-colors">
                      {office.phone}
                    </Link>
                  </div>

                  <div className="flex items-center gap-4">
                    <Mail className="w-5 h-5 text-primary shrink-0 opacity-70" />
                    <Link to={`mailto:${office.email}`} className="text-muted-foreground hover:text-white transition-colors">
                      {office.email}
                    </Link>
                  </div>
                </div>

                <div className="mt-10 pt-6 border-t border-border/20">
                  <Button variant="link" className="p-0 h-auto text-white hover:text-primary group-hover:translate-x-1 transition-transform duration-300" asChild>
                    <Link to="#" className="flex items-center gap-2">
                      View on Map <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </div>

              {}
              {index < offices.length - 1 && <Separator orientation="vertical" className="hidden md:block h-auto min-h-[300px] bg-border/40 mx-4" />}
            </React.Fragment>)}
        </div>

        {}
        <div className="mt-24 pt-16 border-t border-border/20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground/60 mb-6">
                Corporate Headquarters
              </h4>
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-primary mt-1 shrink-0 opacity-70" />
                <div className="text-muted-foreground leading-relaxed">
                  <p>2001 L Street N.W., Suite 500</p>
                  <p>Washington, DC 20036</p>
                  <p className="mt-1">United States</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground/60 mb-6">
                Contact DynEstate®
              </h4>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Phone className="w-5 h-5 text-primary shrink-0 opacity-70" />
                  <Link to="tel:+12125557890" className="text-muted-foreground hover:text-white transition-colors">+1 (202) 964-4766</Link>
                </div>
                <div className="flex items-center gap-4">
                  <Mail className="w-5 h-5 text-primary shrink-0 opacity-70" />
                  <Link to="mailto:contact@dynestate.com" className="text-muted-foreground hover:text-white transition-colors">capital@dynestate.com</Link>
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-end">
              <p className="text-sm text-muted-foreground/60 italic">
                DynEstate® operates as an independent capital marketplace partner.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}